<template>
    <Head title="Plan & Billing | Settings" />
    <div class="px-4 md:px-8" data-page="settings">
        <div class="sm:flex sm:justify-between sm:items-center mb-10">
            <div class="mb-4 sm:mb-0">
                <h1 class="text-2xl md:text-3xl text-gray-800 dark:text-gray-100 font-bold">
                    Account Settings
                </h1>
            </div>
        </div>

        <div
            class="lg:bg-white dark:bg-slate-800 lg:shadow-lg lg:rounded-sm lg:border lg:border-gray-200 dark:border-slate-700 mb-8"
        >
            <div class="flex flex-col lg:flex-row">
                <div
                    class="flex flex-nowrap p-0 lg:px-5 lg:py-10 bg-white dark:bg-slate-800 shadow-lg rounded-sm border border-gray-200 dark:border-slate-700 mb-8 lg:border-none lg:shadow-none"
                >
                    <SettingsNav />
                </div>

                <div
                    class="p-5 lg:px-0 lg:py-0 bg-white dark:bg-slate-800 shadow-lg rounded-sm border border-gray-200 dark:border-slate-700 mb-8 lg:mb-0 lg:border-r-0 lg:border-x-0 lg:border-y-0 lg:!border-l lg:!border-gray-200 dark:border-slate-700 lg:shadow-none"
                    style="grow: 1"
                >
                    <div class="flex flex-nowrap flex-col lg:px-10 lg:py-10">
                        <!-- Page Header -->
                        <div class="mb-8">
                            <h2
                                class="text-xl lg:text-2xl text-gray-800 dark:text-gray-100 font-bold"
                            >
                                Plan & Billing
                            </h2>
                            <p class="text-sm text-slate-500 dark:text-slate-400 mt-1">
                                Manage your 10xTravel plan and check invoices
                            </p>
                        </div>

                        <CurrentPlanCard
                            :is-subscribed="isSubscribed"
                            :is-employee="isEmployee"
                            :subscription-ends="subscriptionEnds"
                            :subscription-price="subscriptionPrice"
                            :is-monthly-subscriber="isMonthlySubscriber"
                            :is-yearly-subscriber="isYearlySubscriber"
                            :is-founders-club-subscriber="isFoundersClubSubscriber"
                        />

                        <PaymentMethodsSection
                            :payment-methods="paymentMethods"
                            :default-payment-method-id="defaultPaymentMethodId"
                            :billing-address="billingAddress"
                            :billing-email="billingEmail"
                            @add-card="openAddCardModal"
                            @edit-card="openEditCardModal"
                            @delete-card="confirmDeleteCard"
                            @set-default="setDefaultCard"
                            @edit-address="editBillingAddress"
                            @edit-email="editBillingEmail"
                        />

                        <InvoiceHistory
                            :invoices="invoices"
                            :visible-invoices="visibleInvoices"
                            :visible-invoice-count="visibleInvoiceCount"
                            :loading-subscription-payments="loadingSubscriptionPayments"
                            @show-more="showMoreInvoices"
                        />

                        <!-- Subscription Management (only for paying subscribers, not employees) -->
                        <section
                            v-if="isSubscribed && !isEmployee"
                            class="mb-8"
                        >
                            <h3
                                class="text-base font-semibold text-slate-900 dark:text-slate-100 mb-3"
                            >
                                Subscription management
                            </h3>

                            <!-- Active subscription: show cancel option -->
                            <div
                                v-if="
                                    subscription?.stripe_status === 'active' &&
                                    !subscription?.on_grace_period
                                "
                                class="flex flex-col sm:flex-row sm:items-center justify-between gap-4 sm:gap-8"
                            >
                                <div class="flex flex-col gap-1 flex-1">
                                    <p
                                        class="text-sm font-medium text-slate-900 dark:text-slate-100"
                                    >
                                        Cancel subscription
                                    </p>
                                    <p class="text-sm text-slate-500 dark:text-slate-400">
                                        If you cancel your plan, you will lose
                                        access to all 10xTravel+ exclusive
                                        features at the end of your current
                                        billing period.
                                    </p>
                                </div>
                                <button
                                    class="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm font-medium text-slate-900 dark:text-slate-100 rounded shadow-sm hover:bg-slate-50 dark:hover:bg-slate-700 dark:bg-slate-900/40 transition-colors whitespace-nowrap"
                                    @click="cancelFlowRef?.open()"
                                >
                                    Cancel subscription
                                </button>
                            </div>

                            <!-- Grace period: show reactivate option -->
                            <div
                                v-if="
                                    subscription?.stripe_status === 'active' &&
                                    subscription?.on_grace_period
                                "
                                class="flex flex-col sm:flex-row sm:items-center justify-between gap-4 sm:gap-8"
                            >
                                <div class="flex flex-col gap-1 flex-1">
                                    <p
                                        class="text-sm font-medium text-slate-900 dark:text-slate-100"
                                    >
                                        Reactivate subscription
                                    </p>
                                    <p class="text-sm text-slate-500 dark:text-slate-400">
                                        Your subscription ends
                                        <strong>{{ subscriptionEnds }}</strong>
                                        . Reactivate to continue using
                                        10xTravel+ service.
                                    </p>
                                </div>
                                <button
                                    class="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-10x-orange text-sm font-medium text-white rounded shadow-sm hover:bg-orange-600 transition-colors whitespace-nowrap"
                                    @click="openReactivateFlow"
                                >
                                    Reactivate
                                </button>
                            </div>
                        </section>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <Teleport to="body">
        <CancelSubscriptionFlow
            ref="cancelFlowRef"
            :subscription="subscription"
            :subscription-ends="subscriptionEnds"
            :is-legacy-subscriber="isLegacySubscriber"
            @cancelled="onCancelled"
        />

        <ReactivateModal
            v-model:show="showReactivateModal"
            :payment-methods="paymentMethods"
            :default-payment-method-id="defaultPaymentMethodId"
            @reactivated="onReactivated"
        />

        <AddPaymentModal
            v-model:show="showAddPaymentModal"
            :title="addCardModalTitle"
            :description="addCardModalDescription"
            @card-added="onCardAdded"
        />

        <EditPaymentCardModal
            v-model:show="showEditCardModal"
            :card="cardToEdit"
            @card-updated="onCardUpdated"
        />

        <DeleteCardModal
            v-model:show="showDeleteCardModal"
            :card="cardToDelete"
            @card-deleted="onCardDeleted"
        />

        <BillingAddressModal
            v-model:show="showBillingAddressModal"
            :billing-address="billingAddress"
            @address-saved="onAddressSaved"
        />

        <BillingEmailModal
            v-model:show="showBillingEmailModal"
            :billing-email="billingEmail"
            @email-saved="onEmailSaved"
        />

        <!-- Success Toast -->
        <Transition
            enter-active-class="transition ease-out duration-300"
            enter-from-class="translate-y-4 opacity-0"
            enter-to-class="translate-y-0 opacity-100"
            leave-active-class="transition ease-in duration-200"
            leave-from-class="translate-y-0 opacity-100"
            leave-to-class="translate-y-4 opacity-0"
        >
            <div
                v-if="toastMessage"
                class="fixed bottom-6 left-6 z-50 flex items-start gap-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg p-4 max-w-sm"
            >
                <svg
                    class="w-6 h-6 text-emerald-500 shrink-0 mt-0.5"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                >
                    <path
                        fill-rule="evenodd"
                        d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm13.36-1.814a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z"
                        clip-rule="evenodd"
                    />
                </svg>
                <div class="flex-1">
                    <p class="text-sm font-semibold text-slate-900 dark:text-slate-100">
                        {{ toastTitle }}
                    </p>
                    <p class="text-sm text-emerald-600">
                        {{ toastMessage }}
                    </p>
                </div>
                <button
                    class="p-1 text-slate-400 hover:text-slate-600 dark:text-slate-300"
                    @click="toastMessage = ''"
                >
                    <svg
                        class="w-4 h-4"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                    >
                        <path
                            fill-rule="evenodd"
                            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                            clip-rule="evenodd"
                        />
                    </svg>
                </button>
            </div>
        </Transition>
    </Teleport>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { Head, usePage, router } from '@inertiajs/vue3';
import { notify } from 'notiwind';
import { storeToRefs } from 'pinia';
import { useTravelPlusStore } from '@/store/travelplus';
import SettingsNav from '@/Pages/Settings/partials/nav.vue';

import CurrentPlanCard from '@/Pages/Settings/PlanBilling/CurrentPlanCard.vue';
import PaymentMethodsSection from '@/Pages/Settings/PlanBilling/PaymentMethodsSection.vue';
import InvoiceHistory from '@/Pages/Settings/PlanBilling/InvoiceHistory.vue';
import CancelSubscriptionFlow from '@/Pages/Settings/PlanBilling/CancelSubscriptionFlow.vue';
import ReactivateModal from '@/Pages/Settings/PlanBilling/ReactivateModal.vue';
import AddPaymentModal from '@/Pages/Settings/PlanBilling/AddPaymentModal.vue';
import EditPaymentCardModal from '@/Pages/Settings/PlanBilling/EditPaymentCardModal.vue';
import DeleteCardModal from '@/Pages/Settings/PlanBilling/DeleteCardModal.vue';
import BillingAddressModal from '@/Pages/Settings/PlanBilling/BillingAddressModal.vue';
import BillingEmailModal from '@/Pages/Settings/PlanBilling/BillingEmailModal.vue';

const page = usePage();
const travelPlusStore = useTravelPlusStore();
const {
    subscription,
    subscriptionPayments,
    subscriptionEnds,
    loadingSubscription,
    loadingSubscriptionPayments,
} = storeToRefs(travelPlusStore);

const paymentMethods = ref([]);
const defaultPaymentMethodId = ref(null);
const billingAddress = ref({});
const billingEmail = ref('');
const invoices = ref([]);
const visibleInvoiceCount = ref(4);

const isSubscribed = computed(() => {
    return page.props.userData?.is_subscribed === true;
});

// Moderators and award bookers get 10xT+ by role, but some also pay for a
// plan; those keep the paid-plan view so they can still cancel or reactivate it.
const isEmployee = computed(() => {
    const userData = page.props.userData;
    const hasCommunityRole = userData?.is_moderator === true || userData?.is_award_booker === true;

    return (
        userData?.is_admin === true ||
        userData?.is_staff === true ||
        (hasCommunityRole && userData?.has_own_subscription !== true)
    );
});

const isLegacySubscriber = computed(() => {
    return page.props.is_legacy_subscriber === true;
});

const isFoundersClubSubscriber = computed(() => {
    return page.props.is_founders_club_subscriber === true;
});

const isMonthlySubscriber = computed(() => {
    return (
        isSubscribed.value &&
        subscription.value?.plan_interval === 'month' &&
        subscription.value?.stripe_status === 'active' &&
        !subscription.value?.on_grace_period &&
        !isLegacySubscriber.value
    );
});

const isYearlySubscriber = computed(() => {
    return (
        isSubscribed.value &&
        subscription.value?.plan_interval === 'year' &&
        subscription.value?.stripe_status === 'active' &&
        !subscription.value?.on_grace_period &&
        !isLegacySubscriber.value
    );
});

const subscriptionPrice = computed(() => {
    const amount = subscription.value?.plan_amount;
    if (amount != null) {
        return Number.isInteger(amount) ? amount.toString() : amount.toFixed(2);
    }
    return '—';
});

const visibleInvoices = computed(() => {
    return invoices.value.slice(0, visibleInvoiceCount.value);
});

const showMoreInvoices = () => {
    visibleInvoiceCount.value += 4;
};

// Modal show/hide refs
const cancelFlowRef = ref(null);
const showReactivateModal = ref(false);
const showAddPaymentModal = ref(false);
const addCardModalTitle = ref('Add payment method');
const addCardModalDescription = ref('');
const addCardCallback = ref(null);
const showEditCardModal = ref(false);
const cardToEdit = ref(null);
const showDeleteCardModal = ref(false);
const cardToDelete = ref(null);
const showBillingAddressModal = ref(false);
const showBillingEmailModal = ref(false);

// Toast state
const toastTitle = ref('');
const toastMessage = ref('');
let toastTimer = null;

const showToast = (title, message, duration = 5000) => {
    toastTitle.value = title;
    toastMessage.value = message;
    if (toastTimer) clearTimeout(toastTimer);
    toastTimer = setTimeout(() => {
        toastMessage.value = '';
    }, duration);
};

// Bridge functions between sub-components
const openAddCardModal = (options = {}) => {
    addCardModalTitle.value = options.title || 'Add payment method';
    addCardModalDescription.value = options.description || '';
    addCardCallback.value = options.callback || null;
    showAddPaymentModal.value = true;
};

const setDefaultCard = async (methodId) => {
    try {
        await ApiService.apiCall(
            `/travel-plus/payment-method/${methodId}/default`,
            'put'
        );
        defaultPaymentMethodId.value = methodId;
    } catch (e) {
        // Error setting default
    }
};

const openEditCardModal = (method) => {
    cardToEdit.value = method;
    showEditCardModal.value = true;
};

const confirmDeleteCard = (method) => {
    cardToDelete.value = method;
    showDeleteCardModal.value = true;
};

const editBillingAddress = () => {
    showBillingAddressModal.value = true;
};

const editBillingEmail = () => {
    showBillingEmailModal.value = true;
};

const openReactivateFlow = () => {
    if (paymentMethods.value.length > 0) {
        showReactivateModal.value = true;
    } else {
        openAddCardModal({
            title: 'Add payment method to continue',
            description:
                'To reactivate your subscription, please add a payment method first. We will not charge anything right now.',
            callback: async () => {
                await travelPlusStore.renewSubscription();
                router.reload();
            },
        });
    }
};

// Event handlers from sub-components
const onCancelled = () => {
    router.reload();
};

const onReactivated = () => {
    router.reload();
};

const onCardAdded = async () => {
    try {
        await loadBillingData();
        if (addCardCallback.value) {
            await addCardCallback.value();
        }
    } catch (e) {
        notify(
            {
                group: 'error',
                title: 'Error',
                text:
                    e?.response?.data?.message ||
                    'Something went wrong completing the action after adding your card. Please try again.',
            },
            10000
        );
    }
};

const onCardUpdated = (data) => {
    const idx = paymentMethods.value.findIndex((m) => m.id === data.id);
    if (idx !== -1) {
        paymentMethods.value[idx].card.exp_month = data.exp_month;
        paymentMethods.value[idx].card.exp_year = data.exp_year;
        paymentMethods.value[idx].billing_details = {
            ...paymentMethods.value[idx].billing_details,
            name: data.name,
        };
    }
};

const onCardDeleted = (cardId) => {
    paymentMethods.value = paymentMethods.value.filter(
        (m) => m.id !== cardId
    );
    if (defaultPaymentMethodId.value === cardId) {
        defaultPaymentMethodId.value = paymentMethods.value[0]?.id || null;
    }
};

const onAddressSaved = async () => {
    await loadBillingData();
};

const onEmailSaved = (email) => {
    billingEmail.value = email;
    showToast(
        'Billing email has been updated',
        'Billing email has been successfully updated'
    );
};

// Data loading
const loadBillingData = async () => {
    try {
        const response = await ApiService.apiCall(
            '/travel-plus/billing',
            'get'
        );
        if (response.data) {
            paymentMethods.value = response.data.payment_methods || [];
            defaultPaymentMethodId.value =
                response.data.default_payment_method || null;
            billingAddress.value = response.data.billing_address || {};
            billingEmail.value = response.data.billing_email || '';
        }
    } catch (e) {
        // Billing data not available yet - that's okay for free users
    }
};

const loadInvoices = async () => {
    await travelPlusStore.getSubscriptionPayments();
    invoices.value = subscriptionPayments.value || [];
};

onMounted(() => {
    travelPlusStore.getSubscription();
    loadBillingData();
    loadInvoices();
});
</script>
